import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Popover } from "antd";
import styled from "styled-components";

import { IconAppSwitcher } from "components/icons";

import AppSwitcher from "./AppSwitcher";
import {
  isAppSwitcherEnabled,
  useAppSwitcherProducts
} from "./hooks/useAppSwitcherProducts";
import { EVA_PRODUCT_ID } from "./products";
import "./AppSwitcherButton.scss";

/**
 * The app switcher's trigger, and the switcher it opens.
 *
 * Renders nothing unless the build is an SSO-bridge one *and*
 * customer-management answered: the suite this switches between only exists
 * behind the bridge, and a button that opened onto an empty or guessed list
 * would be a navigation control that lies. See `useAppSwitcherProducts` for why
 * a failed call and a pending one are treated alike.
 *
 * Belongs beside the brand lockup in the navbar's leading slot, which is where
 * every other Novi application puts it.
 */
function AppSwitcherButton() {
  const products = useAppSwitcherProducts();
  const [open, setOpen] = useState(false);

  // antd's Popover gives outside-click from `trigger="click"` but no key
  // handling, so Escape is this component's own. Registered unconditionally —
  // a hook after a conditional return is a rules-of-hooks violation — and cheap
  // enough to leave mounted while closed.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!isAppSwitcherEnabled() || !products?.length) return null;

  return (
    <>
      {/*
       * Dims the workspace while the switcher is open, leaving the navbar and
       * the panel crisp. `inset` starts at the navbar rather than the viewport
       * top, so the dimming is geometric and no z-index has to be threaded
       * through the navbar to keep it lit. Pointer events are off because
       * antd's own click-catcher already closes the popover; two overlapping
       * catchers would mean the top one silently wins and this looks like dead
       * code. Portaled so a transformed ancestor cannot re-anchor it.
       */}
      {open && createPortal(<Scrim aria-hidden="true" />, document.body)}

      <Popover
        content={
          <AppSwitcher
            products={products}
            currentId={EVA_PRODUCT_ID}
            onClose={() => setOpen(false)}
          />
        }
        overlayClassName="app-switcher-popover"
        placement="bottomLeft"
        trigger="click"
        open={open}
        onOpenChange={setOpen}>
        <TriggerButton
          type="button"
          $open={open}
          title="Switch Novi application"
          aria-label="Switch Novi application"
          aria-haspopup="true"
          aria-expanded={open}
          data-testid="app-switcher-button">
          <IconAppSwitcher size={28} />
        </TriggerButton>
      </Popover>
    </>
  );
}

export default AppSwitcherButton;

const Scrim = styled.div`
  position: fixed;
  inset: var(--navbar-height, 4.5rem) 0 0 0;
  background: rgb(4 28 44 / 28%);
  pointer-events: none;
  z-index: 1000;
`;

const TriggerButton = styled.button<{ $open: boolean }>`
  display: flex;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid
    ${(props) => (props.$open ? "var(--color-primary-40)" : "transparent")};
  border-radius: var(--border-radius);
  background: transparent;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary-40);
    box-shadow: 0 1px 5px var(--color-text-20);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;
